import { useState } from 'react'
import { Search, MoreVertical, Check, CheckCheck, Pin, Bell, Archive } from 'lucide-react'

// 消息列表数据
const initialMessages = [
  {
    id: 1,
    name: '张三',
    avatar: 'ZS',
    avatarColor: 'bg-blue-500',
    lastMessage: '好的，明天见！',
    time: '10:30',
    unread: 2,
    isPinned: true,
    isMuted: false,
    sentByMe: false,
    isRead: false,
  },
  {
    id: 2,
    name: '李四',
    avatar: 'LS',
    avatarColor: 'bg-green-500',
    lastMessage: '文件已经发送了',
    time: '09:45',
    unread: 0,
    isPinned: false,
    isMuted: true,
    sentByMe: true,
    isRead: true,
  },
  {
    id: 3,
    name: '王五',
    avatar: 'WW',
    avatarColor: 'bg-purple-500',
    lastMessage: '晚上一起吃饭吗？',
    time: '昨天',
    unread: 5,
    isPinned: false,
    isMuted: false,
    sentByMe: false,
    isRead: false,
  },
  {
    id: 4,
    name: '项目群',
    avatar: 'XM',
    avatarColor: 'bg-orange-500',
    lastMessage: '赵六: 进度更新一下',
    time: '昨天',
    unread: 12,
    isPinned: true,
    isMuted: false,
    sentByMe: false,
    isRead: false,
  },
  {
    id: 5,
    name: '技术团队',
    avatar: 'JS',
    avatarColor: 'bg-cyan-500',
    lastMessage: '你: 收到，马上处理',
    time: '周一',
    unread: 0,
    isPinned: false,
    isMuted: true,
    sentByMe: true,
    isRead: true,
  },
  {
    id: 6,
    name: '客户A',
    avatar: 'KH',
    avatarColor: 'bg-red-500',
    lastMessage: '报价单请发给我',
    time: '周一',
    unread: 1,
    isPinned: false,
    isMuted: false,
    sentByMe: false,
    isRead: false,
  },
  {
    id: 7,
    name: '家人群',
    avatar: 'JR',
    avatarColor: 'bg-pink-500',
    lastMessage: '周末回家吃饭',
    time: '周日',
    unread: 0,
    isPinned: false,
    isMuted: false,
    sentByMe: true,
    isRead: true,
  },
  {
    id: 8,
    name: '产品组',
    avatar: 'CP',
    avatarColor: 'bg-indigo-500',
    lastMessage: '需求文档更新了',
    time: '周六',
    unread: 3,
    isPinned: false,
    isMuted: true,
    sentByMe: false,
    isRead: false,
  },
]

export default function Messages() {
  const [searchQuery, setSearchQuery] = useState('')
  const [messages] = useState(initialMessages)

  const filteredMessages = messages.filter(
    (msg) =>
      msg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      msg.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const pinnedMessages = filteredMessages.filter((msg) => msg.isPinned)
  const regularMessages = filteredMessages.filter((msg) => !msg.isPinned)

  return (
    <div className="animate-fade-in">
      {/* 搜索栏 */}
      <div className="px-4 pt-4 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-telegram-secondary" />
          <input
            type="text"
            placeholder="搜索消息"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-telegram-dark rounded-xl text-telegram-text placeholder-telegram-secondary focus:outline-none focus:ring-2 focus:ring-telegram-blue/50"
          />
        </div>
      </div>

      {/* 已归档聊天 */}
      <div className="px-4 py-2">
        <button className="flex items-center justify-between w-full p-3 rounded-xl bg-telegram-dark/50 active:bg-telegram-dark transition-colors">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-telegram-secondary/20 rounded-lg">
              <Archive className="w-5 h-5 text-telegram-secondary" />
            </div>
            <span className="text-telegram-text font-medium">已归档聊天</span>
          </div>
          <span className="text-sm text-telegram-secondary">5</span>
        </button>
      </div>

      {/* 消息列表 */}
      <div className="px-4 pb-4">
        {/* 置顶聊天 */}
        {pinnedMessages.length > 0 && (
          <div className="mb-4">
            <h3 className="text-xs font-semibold text-telegram-secondary uppercase tracking-wider mb-2 px-1">
              置顶聊天
            </h3>
            <div className="space-y-1">
              {pinnedMessages.map((message) => (
                <MessageItem key={message.id} message={message} />
              ))}
            </div>
          </div>
        )}

        {/* 普通聊天 */}
        <div>
          {regularMessages.length > 0 && pinnedMessages.length > 0 && (
            <h3 className="text-xs font-semibold text-telegram-secondary uppercase tracking-wider mb-2 px-1">
              所有聊天
            </h3>
          )}
          <div className="space-y-1">
            {regularMessages.map((message) => (
              <MessageItem key={message.id} message={message} />
            ))}
          </div>
        </div>

        {/* 空状态 */}
        {filteredMessages.length === 0 && (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-telegram-secondary mx-auto mb-3" />
            <p className="text-telegram-secondary">未找到相关聊天</p>
          </div>
        )}
      </div>
    </div>
  )
}

// 单个消息项组件
interface Message {
  id: number
  name: string
  avatar: string
  avatarColor: string
  lastMessage: string
  time: string
  unread: number
  isPinned: boolean
  isMuted: boolean
  sentByMe: boolean
  isRead: boolean
}

function MessageItem({ message }: { message: Message }) {
  return (
    <button className="w-full flex items-center p-3 rounded-xl bg-telegram-dark hover:bg-telegram-dark/80 active:bg-telegram-dark/60 transition-colors">
      {/* 头像 */}
      <div className={`w-12 h-12 ${message.avatarColor} rounded-full flex items-center justify-center flex-shrink-0`}>
        <span className="text-white font-semibold text-sm">{message.avatar}</span>
      </div>

      {/* 内容 */}
      <div className="flex-1 min-w-0 ml-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-telegram-text truncate">{message.name}</h4>
          <div className="flex items-center space-x-1 ml-2">
            {message.isMuted && <Bell className="w-3 h-3 text-telegram-secondary" />}
            {message.isPinned && <Pin className="w-3 h-3 text-telegram-secondary fill-current" />}
            <span className="text-xs text-telegram-secondary whitespace-nowrap">{message.time}</span>
          </div>
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <p className="text-sm text-telegram-secondary truncate pr-2">
            {message.sentByMe && (
              <span className="inline-flex items-center mr-1">
                {message.isRead ? (
                  <CheckCheck className="w-3 h-3 text-blue-400" />
                ) : (
                  <Check className="w-3 h-3 text-telegram-secondary" />
                )}
              </span>
            )}
            {message.lastMessage}
          </p>
          {message.unread > 0 && (
            <span className="flex-shrink-0 min-w-[20px] h-5 px-1.5 bg-blue-500 rounded-full text-xs text-white font-medium flex items-center justify-center">
              {message.unread > 99 ? '99+' : message.unread}
            </span>
          )}
        </div>
      </div>

      {/* 更多选项 */}
      <button className="ml-2 p-1 rounded-full hover:bg-telegram-secondary/20 transition-colors">
        <MoreVertical className="w-4 h-4 text-telegram-secondary" />
      </button>
    </button>
  )
}
